import { Box, Chip, Divider, Grid, Stack, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * A null total means the data could not support the figure — render a dash and
 * lean on totals.notes for the reason. Never show it as zero.
 */
function Total({ label, value, format }) {
  const missing = value === null || value === undefined;

  let text = '—';
  if (!missing) {
    if (format === 'currency') text = `₹${Number(value).toLocaleString()}`;
    else if (format === 'decimal') text = Number(value).toFixed(2);
    else text = Number(value).toLocaleString();
  }

  return (
    <Grid item xs={6} md={3}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, color: missing ? 'text.disabled' : 'inherit' }}>
        {text}
      </Typography>
    </Grid>
  );
}

function PlanPreviewPanel({ plan }) {
  if (!plan) return null;

  const { totals } = plan;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {plan.title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {plan.objective}
      </Typography>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Total label="Creators" value={totals.creator_count} />
        <Total label="Est. reach" value={totals.estimated_reach} />
        <Total label="Est. cost" value={totals.estimated_cost} format="currency" />
        <Total label="Est. CPV" value={totals.estimated_cpv} format="decimal" />
      </Grid>

      {totals.notes && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          {totals.notes}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      {plan.sections.map((section) => (
        <Box key={section.heading} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {section.heading}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {section.body}
          </Typography>
          {section.items.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {section.items.map((item) => (
                <li key={item}>
                  <Typography variant="body2">{item}</Typography>
                </li>
              ))}
            </ul>
          )}
        </Box>
      ))}

      {plan.risks.length > 0 && (
        <Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <WarningAmberIcon fontSize="small" sx={{ color: '#8a6d00' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Risks
            </Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 0.75 }}>
            {plan.risks.map((risk) => (
              <Chip
                key={risk}
                label={risk}
                size="small"
                sx={{
                  bgcolor: '#fff7db',
                  color: '#8a6d00',
                  height: 'auto',
                  justifyContent: 'flex-start',
                  '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export default PlanPreviewPanel;
